(function () {
  const DESKTOP_MEDIA_QUERY = '(min-width: 1024px)';

  const BLOCK_TAGS = new Set([
    'P', 'UL', 'OL', 'LI', 'TABLE', 'TBODY', 'THEAD', 'TFOOT', 'TR', 'TD', 'TH',
    'DIV', 'SECTION', 'ARTICLE', 'ASIDE', 'BLOCKQUOTE', 'FIGURE', 'FIGCAPTION',
    'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'PRE', 'ADDRESS', 'DL', 'DT', 'DD', 'HR',
    'DETAILS', 'SUMMARY', 'FIELDSET', 'FORM',
  ]);

  // Publisher blank lines can cross inline tags; DOM ranges retain valid formatting.
  function normalizePublisherContent(root = document) {
    const containers = Array.from(root.querySelectorAll('[data-publisher-content]'));
    if (root.matches?.('[data-publisher-content]')) containers.unshift(root);
    const hasContent = (node) => node.textContent.trim() !== ''
      || !!node.querySelector('img, svg, video, audio, iframe');

    const appendParagraphs = (nodes, output, existingParagraph = null) => {
      if (!nodes.length) return;
      const source = document.createElement('div');
      nodes.forEach((node) => source.appendChild(node));
      if (!hasContent(source)) return;
      const walker = document.createTreeWalker(source, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT);
      const separators = [];
      let breaks = [];
      const flushBreaks = () => {
        if (breaks.length > 1) separators.push(breaks);
        breaks = [];
      };
      let node = walker.nextNode();
      while (node) {
        if (node.nodeType === Node.ELEMENT_NODE && node.tagName === 'BR') {
          breaks.push(node);
        } else if ((node.nodeType === Node.TEXT_NODE && node.textContent.trim() !== '')
          || (node.nodeType === Node.ELEMENT_NODE && node.matches('img, svg, video, audio, iframe'))) {
          flushBreaks();
        }
        node = walker.nextNode();
      }
      flushBreaks();
      if (existingParagraph && !separators.length) {
        existingParagraph.append(...source.childNodes);
        output.appendChild(existingParagraph);
        return;
      }

      let paragraphCount = 0;
      const range = document.createRange();
      range.setStart(source, 0);
      const appendRange = () => {
        const fragment = range.cloneContents();
        if (!hasContent(fragment)) return;
        // Range boundaries can leave empty clones of inline ancestors.
        Array.from(fragment.querySelectorAll('b, strong, i, em, span, a')).reverse().forEach((element) => {
          if (!element.textContent.trim() && !element.querySelector('br, img, svg, video, audio, iframe')) {
            element.replaceWith(document.createTextNode(element.textContent));
          }
        });
        const paragraph = existingParagraph ? existingParagraph.cloneNode(false) : document.createElement('p');
        if (paragraphCount > 0) paragraph.removeAttribute('id');
        paragraphCount += 1;
        paragraph.setAttribute('data-publisher-paragraph', '');
        paragraph.appendChild(fragment);
        output.appendChild(paragraph);
      };
      separators.forEach((separator) => {
        range.setEndBefore(separator[0]);
        appendRange();
        range.setStartAfter(separator[separator.length - 1]);
      });
      range.setEnd(source, source.childNodes.length);
      appendRange();
    };

    const normalizeContainer = (container) => {
      const output = document.createDocumentFragment();
      let inlineNodes = [];
      const flushInlineNodes = () => {
        appendParagraphs(inlineNodes, output);
        inlineNodes = [];
      };
      Array.from(container.childNodes).forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE && BLOCK_TAGS.has(node.tagName)) {
          flushInlineNodes();
          // Retain semantic blocks; split only paragraph content that still uses blank lines.
          if (node.tagName === 'P') {
            appendParagraphs(Array.from(node.childNodes), output, node);
            return;
          }
          if (node.tagName === 'DIV' && !node.classList.contains('product__overview-love-of-reading')) {
            normalizeContainer(node);
            if (!hasContent(node)) return;
          }
          output.appendChild(node);
        } else {
          inlineNodes.push(node);
        }
      });
      flushInlineNodes();
      container.replaceChildren(output);
    };
    containers.forEach(normalizeContainer);
  }

  window.normalizePublisherContent = normalizePublisherContent;

  const IMAGE_SEGMENT_WEIGHT = 50;
  const FADE_DURATION = 200;
  const TOTAL_ANIMATION_DURATION = FADE_DURATION * 2;
  const OVERFLOW_TOLERANCE_PX = 10;

  let productOverviewInstance;

  class ProductOverview {
    constructor() {
      this.handleResize = this.handleResize.bind(this);
      this.handleMediaChange = this.handleMediaChange.bind(this);
      this.desktopMediaQuery = window.matchMedia(DESKTOP_MEDIA_QUERY);
      this.readMoreHandlers = new WeakMap();
      this.editorialReviewHandlers = new WeakMap();
      this.twoColumnContainers = [];
      this.hasLoggedPreviewMapParseError = false;
      this.hasLoggedSelectedVariantParseError = false;
      this.hasLoggedVariantTagsParseError = false;
      this.variantDescriptionHandler = null;
      this.variantPreviewHandler = null;
      this.variantTagsHandler = null;
      this.previewButtonClickHandler = null;
      this.previewModalClosedHandler = null;
      this.previewButton = null;
      this.abortController = null;
      this.pendingVariantId = null;
      this.resizeListenerAttached = false;
      this.mediaQueryListenerAttached = false;
      this.isInitializing = false;
      this.init();
    }

    /**
     * Cleans up all event listeners and aborts pending requests.
     *
     * Called automatically at the start of `init()` to prevent memory leaks and
     * duplicate listeners. Also aborts any pending fetch requests via AbortController.
     *
     * Side effects: Removes all event listeners (variant:change, resize, media query),
     * cancels pending fetches, and resets handler references.
     * Safe to call multiple times (idempotent).
     */
    cleanup() {
      // Remove variant:change event listeners
      if (this.variantDescriptionHandler) {
        document.removeEventListener('variant:change', this.variantDescriptionHandler);
        this.variantDescriptionHandler = null;
      }
      if (this.variantPreviewHandler) {
        document.removeEventListener('variant:change', this.variantPreviewHandler);
        this.variantPreviewHandler = null;
      }
      if (this.variantTagsHandler) {
        document.removeEventListener('variant:change', this.variantTagsHandler);
        this.variantTagsHandler = null;
      }
      if (this.previewButton && this.previewButtonClickHandler) {
        this.previewButton.removeEventListener('click', this.previewButtonClickHandler);
        this.previewButtonClickHandler = null;
        this.previewButton = null;
      }
      if (this.previewModalClosedHandler) {
        document.body.removeEventListener('modalClosed', this.previewModalClosedHandler);
        this.previewModalClosedHandler = null;
      }

      // Remove resize listener
      if (this.resizeListenerAttached) {
        window.removeEventListener('resize', this.handleResize);
        this.resizeListenerAttached = false;
      }

      // Remove media query listener
      if (this.mediaQueryListenerAttached && this.desktopMediaQuery?.removeEventListener) {
        this.desktopMediaQuery.removeEventListener('change', this.handleMediaChange);
        this.mediaQueryListenerAttached = false;
      }

      // Cancel any pending fetch requests
      this.abortController?.abort();
      this.abortController = null;
      this.pendingVariantId = null;
    }

    init() {
      // Prevent concurrent initialization calls
      if (this.isInitializing) {
        return;
      }
      this.isInitializing = true;

      try {
        // Clean up existing listeners before re-initializing
        this.cleanup();

        // Set up resize listener
        if (!this.resizeListenerAttached) {
          window.addEventListener('resize', this.handleResize);
          this.resizeListenerAttached = true;
        }

        // Set up media query listener
        if (!this.mediaQueryListenerAttached && this.desktopMediaQuery?.addEventListener) {
          this.desktopMediaQuery.addEventListener('change', this.handleMediaChange);
          this.mediaQueryListenerAttached = true;
        }

        normalizePublisherContent();
        this.cacheTwoColumnContainers();
        this.applyTwoColumnLayout(this.desktopMediaQuery?.matches ?? false);
        this.refreshExpandableText();
        this.setupPreviewButton();
        this.setupVariantTags();
        this.setupVariantDescription();
      } finally {
        this.isInitializing = false;
      }
    }

    /**
     * Escapes HTML special characters to prevent XSS
     */
    escapeHtml(str) {
      if (!str) return '';
      const div = document.createElement('div');
      div.textContent = str;
      return div.innerHTML;
    }

    cacheTwoColumnContainers() {
      try {
        this.twoColumnContainers = Array.from(document.querySelectorAll('.product__overview-text .text-content.two-columns'));
        this.twoColumnContainers.forEach((container) => {
          if (!container.dataset.originalMarkup) {
            container.dataset.originalMarkup = container.innerHTML;
          }
        });
      } catch (e) {
        this.twoColumnContainers = [];
      }
    }

    handleMediaChange(event) {
      this.applyTwoColumnLayout(event.matches, { force: true });
      this.refreshExpandableText();
    }

    // Re-evaluates both truncation affordances (overview + editorial review)
    refreshExpandableText() {
      this.setupReadMoreButtons();
      this.setupEditorialReviewToggles();
    }

    applyTwoColumnLayout(shouldEnable, { force = false } = {}) {
      if (!this.twoColumnContainers || this.twoColumnContainers.length === 0) return;

      this.twoColumnContainers.forEach((container) => {
        if (!container) return;

        const textContainer = container.closest('.truncated-text');
        const isExpanded = textContainer && textContainer.classList.contains('expanded');

        if (shouldEnable && isExpanded) {
          this.enableTwoColumnLayout(container, { force });
        } else {
          this.disableTwoColumnLayout(container);
        }
      });
    }

    enableTwoColumnLayout(container, { force = false } = {}) {
      if (!container) return;
      if (!force && container.dataset.columnsReady === 'true') return;

      const sourceMarkup = container.dataset.originalMarkup || container.innerHTML || '';
      const trimmedMarkup = sourceMarkup.trim();
      if (!trimmedMarkup) {
        container.removeAttribute('data-columns-ready');
        container.innerHTML = '';
        return;
      }

      const segments = this.createSegmentsFromMarkup(trimmedMarkup);

      if (segments.length <= 1) {
        container.innerHTML = trimmedMarkup;
        container.removeAttribute('data-columns-ready');
        return;
      }

      const segmentDetails = this.buildSegmentDetails(segments);
      const { primarySegments, secondarySegments } = this.splitSegments(segmentDetails);

      const primaryColumn = document.createElement('div');
      primaryColumn.className = 'product__overview-column';
      const secondaryColumn = document.createElement('div');
      secondaryColumn.className = 'product__overview-column';

      container.innerHTML = '';
      container.append(primaryColumn, secondaryColumn);

      this.appendSegmentsToColumn(primaryColumn, primarySegments);
      this.appendSegmentsToColumn(secondaryColumn, secondarySegments);

      container.dataset.columnsReady = 'true';
    }

    disableTwoColumnLayout(container) {
      if (!container) return;

      // Always restore originalMarkup if it exists, even if columnsReady !== 'true'
      // This ensures HTML content is properly rendered when collapsing or on mobile
      const originalMarkup = container.dataset.originalMarkup || '';
      if (originalMarkup.trim()) {
        container.innerHTML = originalMarkup;
      } else {
        // If originalMarkup is missing, try to recover by checking DOM structure
        // Check for column structure in DOM rather than relying solely on the flag
        const columns = container.querySelectorAll('.product__overview-column');
        if (columns.length > 0) {
          // Content is column-structured, extract and reconstruct original
          const extractedContent = Array.from(columns)
            .map((col) => col.innerHTML.trim())
            .filter((html) => html)
            .join('');
          if (extractedContent) {
            container.innerHTML = extractedContent;
            container.dataset.originalMarkup = extractedContent;
            console.warn('[ProductOverview] Recovered originalMarkup from column structure');
          } else {
            container.innerHTML = '';
          }
        } else {
          // No column structure detected, current content should be safe to use as originalMarkup
          const currentContent = container.innerHTML || '';
          if (currentContent.trim()) {
            container.dataset.originalMarkup = currentContent;
          } else {
            container.innerHTML = '';
          }
        }
      }
      container.removeAttribute('data-columns-ready');
    }

    createSegmentsFromMarkup(markup) {
      const template = document.createElement('template');
      template.innerHTML = markup;

      const segments = [];
      let buffer = [];

      const flushBuffer = () => {
        if (buffer.length === 0) return;
        segments.push(buffer);
        buffer = [];
      };

      Array.from(template.content.childNodes).forEach((node) => {
        if (node.nodeType === Node.COMMENT_NODE) return;

        if (node.nodeType === Node.ELEMENT_NODE) {
          const tagName = node.tagName.toUpperCase();

          if (tagName === 'BR') {
            const lastItem = buffer[buffer.length - 1];
            if (lastItem && lastItem.nodeType === Node.ELEMENT_NODE && lastItem.tagName.toUpperCase() === 'BR') {
              buffer.pop();
              flushBuffer();
            } else {
              buffer.push(node);
            }
            return;
          }

          if (BLOCK_TAGS.has(tagName)) {
            flushBuffer();
            segments.push([node]);
            return;
          }

          buffer.push(node);
          return;
        }

        if (node.nodeType === Node.TEXT_NODE) {
          if (!node.textContent || node.textContent.trim().length === 0) {
            if (buffer.length === 0) return;
          }
          buffer.push(node);
          return;
        }

        buffer.push(node);
      });

      flushBuffer();
      return segments;
    }

    createInlineWrapper(items) {
      const wrapper = document.createElement('p');
      items.forEach((item) => wrapper.appendChild(item));
      return wrapper;
    }

    isInlineSegment(items) {
      return items.every((item) => {
        if (item.nodeType === Node.COMMENT_NODE) return false;
        if (item.nodeType === Node.TEXT_NODE) return true;
        if (item.nodeType !== Node.ELEMENT_NODE) return true;
        const tag = item.tagName.toUpperCase();
        if (tag === 'BR') return true;
        return !BLOCK_TAGS.has(tag);
      });
    }

    buildSegmentFragment(segmentNodes) {
      const hasSingleBlock = segmentNodes.length === 1
        && segmentNodes[0].nodeType === Node.ELEMENT_NODE
        && BLOCK_TAGS.has(segmentNodes[0].tagName.toUpperCase());

      if (hasSingleBlock) return segmentNodes[0];

      if (this.isInlineSegment(segmentNodes)) return this.createInlineWrapper(segmentNodes);

      const wrapper = document.createElement('div');
      segmentNodes.forEach((item) => wrapper.appendChild(item));
      return wrapper;
    }

    getSegmentWeight(items) {
      return items.reduce((total, item) => {
        if (item.nodeType === Node.TEXT_NODE) {
          const content = item.textContent || '';
          return total + content.trim().length;
        }
        if (item.nodeType === Node.ELEMENT_NODE) {
          const tag = item.tagName ? item.tagName.toUpperCase() : '';
          if (tag === 'IMG') return total + IMAGE_SEGMENT_WEIGHT;
          return total + (item.textContent || '').trim().length;
        }
        return total;
      }, 0);
    }

    buildSegmentDetails(segments) {
      return segments.map((segmentNodes) => ({
        nodes: segmentNodes,
        weight: Math.max(this.getSegmentWeight(segmentNodes), 1),
      }));
    }

    /**
     * Preserve reading order while aiming for a balanced visual height across columns.
     * The split point is the first segment to push cumulative weight past half the total.
     */
    splitSegments(segmentDetails) {
      if (segmentDetails.length <= 1) {
        return {
          primarySegments: [...segmentDetails],
          secondarySegments: [],
        };
      }

      const totalWeight = segmentDetails.reduce((sum, item) => sum + item.weight, 0);
      const targetWeight = totalWeight / 2;
      let runningWeight = 0;
      let splitIndex = segmentDetails.length;

      for (let i = 0; i < segmentDetails.length; i += 1) {
        const segment = segmentDetails[i];
        runningWeight += segment.weight;
        const remaining = segmentDetails.length - i - 1;
        if (runningWeight >= targetWeight && remaining > 0) {
          splitIndex = i + 1;
          break;
        }
      }

      if (splitIndex === segmentDetails.length) {
        splitIndex = Math.ceil(segmentDetails.length / 2);
      }

      const primarySegments = segmentDetails.slice(0, splitIndex);
      const secondarySegments = segmentDetails.slice(splitIndex);

      if (secondarySegments.length === 0 && primarySegments.length > 1) {
        secondarySegments.unshift(primarySegments.pop());
      }

      return { primarySegments, secondarySegments };
    }

    appendSegmentsToColumn(column, segments) {
      segments.forEach((segment) => {
        const fragment = this.buildSegmentFragment(segment.nodes);
        if (!fragment) return;
        column.appendChild(fragment);
      });
    }

    setupReadMoreButtons() {
      const buttons = document.querySelectorAll('.js-read-more-btn');
      buttons.forEach((button) => {
        this.checkTruncationNeeded(button);
        if (this.readMoreHandlers.has(button)) return;
        const handler = () => this.toggleReadMore(button);
        button.addEventListener('click', handler);
        this.readMoreHandlers.set(button, handler);
      });
    }

    // Sets up the "Read full review" toggle for the Editorial Review module
    // (separate from .js-read-more-btn / checkTruncationNeeded above)
    setupEditorialReviewToggles() {
      const buttons = document.querySelectorAll('.js-editorial-review-toggle');
      buttons.forEach((button) => {
        this.checkEditorialReviewTruncation(button);
        if (this.editorialReviewHandlers.has(button)) return;
        const handler = () => this.expandEditorialReview(button);
        button.addEventListener('click', handler);
        this.editorialReviewHandlers.set(button, handler);
      });
    }

    checkEditorialReviewTruncation(button) {
      const root = button.closest('.editorial-review, .product__overview-review');
      const body = root?.querySelector('.js-editorial-review-body');
      if (!root || !body) return;

      // Clamping is CSS-only; side-by-side layout has no overflow, so this self-resolves
      const needsTruncation = body.scrollHeight > body.clientHeight + OVERFLOW_TOLERANCE_PX;
      button.style.display = needsTruncation ? 'block' : 'none';

      // Content already fits: don't hide the signature/badge waiting for a click that won't come
      root.classList.toggle('content-fits', !needsTruncation);
    }

    // Expansion is one-way; no "read less" affordance
    expandEditorialReview(button) {
      const root = button.closest('.editorial-review, .product__overview-review');
      if (!root) return;

      root.classList.add('is-expanded');
      button.setAttribute('aria-expanded', 'true');
      button.style.display = 'none';
    }

    setupPreviewButton() {
      try {
        const productInfo = document.querySelector("product-info[id^='MainProduct-']");
        if (!productInfo) return;

        const sectionId = productInfo.dataset.section;
        const mapEl = document.getElementById(`VariantPreviewURLs-${sectionId}`);
        const previewButton = document.querySelector('.product__overview-audio-sample');
        const previewModal = document.getElementById(`PreviewModal-${sectionId}`);
        const previewDialog = previewModal?.querySelector('[role="dialog"]');
        const freeChapterLink = document.querySelector('.product__overview-free-chapter');
        const iframe = previewModal?.querySelector('.product-preview-iframe');
        const audioView = previewModal?.querySelector('[data-preview-audio]');
        const audioPlayer = audioView?.querySelector('audio');
        const previewLabel = previewButton?.querySelector('[data-preview-label]');
        const audiobookIcon = previewButton?.querySelector('[data-preview-icon-audiobook]');
        const ebookIcon = previewButton?.querySelector('[data-preview-icon-ebook]');

        if (
          !mapEl || !previewButton || !previewModal || !previewDialog
          || !iframe || !audioView || !audioPlayer || !previewLabel
        ) return;

        let previewMap = {};
        try {
          const arr = JSON.parse(mapEl.textContent || '[]');
          previewMap = arr.reduce((acc, item) => {
            if (item && item.id && item.url) {
              acc[item.id] = {
                type: item.type === 'audiobook' ? 'audiobook' : 'ebook',
                url: item.url,
              };
            }
            return acc;
          }, {});
        } catch (e) {
          if (!this.hasLoggedPreviewMapParseError) {
            console.warn('ProductOverview: Failed to parse VariantPreviewURLs JSON', e);
            this.hasLoggedPreviewMapParseError = true;
          }
        }

        const getSelectedVariantId = () => {
          try {
            const sv = document.querySelector('variant-selects [data-selected-variant]');
            if (sv) {
              const json = sv.textContent || sv.innerHTML || '';
              const data = JSON.parse(json);
              if (data?.id) return data.id;
            }

            return productInfo.querySelector('input[name="id"]')?.value || null;
          } catch (e) {
            if (!this.hasLoggedSelectedVariantParseError) {
              console.warn('ProductOverview: Failed to parse selected variant JSON (preview)', e);
              this.hasLoggedSelectedVariantParseError = true;
            }
            return null;
          }
        };

        const resetAudio = ({ clearSource = false } = {}) => {
          audioPlayer.pause();
          try {
            audioPlayer.currentTime = 0;
          } catch (e) {
            // Media metadata may not have loaded yet.
          }
          if (clearSource) {
            audioPlayer.removeAttribute('src');
            audioPlayer.load();
          }
        };

        const clearPreviewMedia = () => {
          iframe.setAttribute('src', '');
          resetAudio({ clearSource: true });
        };

        const updatePreviewState = (variantId = getSelectedVariantId()) => {
          const preview = previewMap[variantId];
          const hasPreview = !!preview?.url;
          const isAudiobook = preview?.type === 'audiobook';
          const label = isAudiobook
            ? previewButton.dataset.audiobookLabel
            : previewButton.dataset.ebookLabel;

          previewButton.classList.toggle('hide', !hasPreview);
          previewButton.toggleAttribute('hidden', !hasPreview);
          previewButton.toggleAttribute('disabled', !hasPreview);
          previewButton.setAttribute('aria-hidden', hasPreview ? 'false' : 'true');
          previewLabel.textContent = label || '';
          audiobookIcon?.toggleAttribute('hidden', !isAudiobook);
          ebookIcon?.toggleAttribute('hidden', isAudiobook);

          if (freeChapterLink) {
            freeChapterLink.toggleAttribute('hidden', !hasPreview);
            freeChapterLink.setAttribute('aria-hidden', hasPreview ? 'false' : 'true');
          }

          if (!hasPreview) clearPreviewMedia();
        };

        // Initial state
        updatePreviewState();

        this.previewButton = previewButton;
        this.previewButtonClickHandler = (e) => {
          e.preventDefault();
          const variantId = getSelectedVariantId();
          const preview = previewMap[variantId];
          if (!preview?.url) return;

          const isAudiobook = preview.type === 'audiobook';
          previewModal.classList.toggle('product-popup-modal--audio-preview', isAudiobook);
          previewDialog.setAttribute(
            'aria-label',
            isAudiobook ? previewButton.dataset.audiobookLabel : previewButton.dataset.ebookLabel,
          );

          if (isAudiobook) {
            iframe.setAttribute('src', '');
            iframe.setAttribute('hidden', '');
            audioView.removeAttribute('hidden');
            audioPlayer.setAttribute('src', preview.url);
            audioPlayer.load();
          } else {
            resetAudio({ clearSource: true });
            audioView.setAttribute('hidden', '');
            iframe.removeAttribute('hidden');
            iframe.setAttribute('src', preview.url);
          }

          previewModal?.show(previewButton);

          if (isAudiobook) {
            audioPlayer.play().catch(() => {
              // Native controls remain available when autoplay is blocked.
            });
          }
        };
        previewButton.addEventListener('click', this.previewButtonClickHandler);

        this.previewModalClosedHandler = () => {
          setTimeout(() => {
            if (!previewModal.hasAttribute('open')) clearPreviewMedia();
          }, 0);
        };
        document.body.addEventListener('modalClosed', this.previewModalClosedHandler);

        // Remove existing listener if it exists to prevent duplicates
        if (this.variantPreviewHandler) {
          document.removeEventListener('variant:change', this.variantPreviewHandler);
        }

        // Respond to variant change events
        this.variantPreviewHandler = (event) => {
          if (previewModal.hasAttribute('open')) previewModal.hide();
          updatePreviewState(event?.detail?.variant?.id || getSelectedVariantId());
        };
        document.addEventListener('variant:change', this.variantPreviewHandler);
      } catch (e) {
        // fail silently
      }
    }

    setupVariantTags() {
      try {
        const tagsContainer = document.querySelector('[data-overview-tags]');
        if (!tagsContainer) return;

        const mapEl = document.querySelector('[data-variant-overview-tags]');
        if (!mapEl) return;

        let variantTagsMap = {};
        let productBadges = [];
        try {
          const arr = JSON.parse(mapEl.textContent || '[]');
          variantTagsMap = arr.reduce((acc, item) => {
            if (item && item.id) {
              acc[item.id] = {
                age: item.age || '',
                indigoexclusive: !!item.indigoexclusive,
                islargeprint: !!item.islargeprint,
                pagecount: item.pagecount || '',
                isKoboAudiobook: !!item.isKoboAudiobook,
                hasKoboIsAbriged: item.hasKoboIsAbriged === true,
                koboIsAbriged: item.koboIsAbriged,
                koboDuration: item.koboDuration || '',
                badges: Array.isArray(item.badges) ? item.badges : [],
              };
            }
            return acc;
          }, {});
        } catch (e) {
          if (!this.hasLoggedVariantTagsParseError) {
            console.warn('ProductOverview: Failed to parse VariantOverviewTags JSON', e);
            this.hasLoggedVariantTagsParseError = true;
          }
        }

        const getSelectedVariantId = () => {
          try {
            const sv = document.querySelector('variant-selects [data-selected-variant]') ?? document.querySelector('product-info [data-selected-variant]');
            if (sv) {
              const json = sv.textContent || sv.innerHTML || '';
              const data = JSON.parse(json);
              if (data?.id) return data.id;
            }

            return document.querySelector("product-info[id^='MainProduct-'] input[name='id']")?.value || null;
          } catch (e) {
            if (!this.hasLoggedSelectedVariantParseError) {
              console.warn('ProductOverview: Failed to parse selected variant JSON (tags)', e);
              this.hasLoggedSelectedVariantParseError = true;
            }
            return null;
          }
        };

        try {
          productBadges = JSON.parse(tagsContainer.getAttribute('data-product-badges') || '[]');
          if (!Array.isArray(productBadges)) productBadges = [];
        } catch (e) {
          productBadges = [];
        }

        const renderTags = (variantId) => {
          const meta = variantTagsMap[variantId];
          if (!meta) {
            tagsContainer.innerHTML = '';
            tagsContainer.setAttribute('hidden', 'true');
            return;
          }

          const labelIndigoExclusive = tagsContainer.dataset.tagIndigoExclusive || 'Indigo Exclusive';
          const labelLargePrint = tagsContainer.dataset.tagLargePrint || 'Large Print';
          const labelPages = tagsContainer.dataset.tagPages || 'Pages';
          const labelUnabridged = tagsContainer.dataset.tagUnabridged || 'Unabridged';
          const audioTagIcon = tagsContainer.dataset.audioTagIcon || '';
          const durationTagIcon = tagsContainer.dataset.durationTagIcon || '';

          const fragments = [];
          const badges = (meta.badges && meta.badges.length ? meta.badges : productBadges);
          const productAge = (tagsContainer.getAttribute('data-product-age') || '').trim();
          const productLanguage = (tagsContainer.getAttribute('data-product-language') || '').trim();
          const age = (meta.age || '').trim();
          const resolvedAge = age || productAge;
          const seen = new Set();
          const pushTag = (val, { icon = '', modifier = '', iconModifier = '' } = {}) => {
            if (!val) return;
            const key = String(val).toUpperCase();
            if (seen.has(key)) return;
            seen.add(key);
            const tagClass = modifier ? ` product__overview-tag--${modifier}` : '';
            const iconClass = iconModifier ? ` product__overview-tag-icon--${iconModifier}` : '';
            const iconSize = iconModifier === 'clock' ? 13 : 16;
            const iconMarkup = icon
              ? `<span class="product__overview-tag-icon${iconClass}" aria-hidden="true"><img src="${this.escapeHtml(icon)}" alt="" width="${iconSize}" height="${iconSize}"></span>`
              : '';
            fragments.push(`<span class="product__overview-tag${tagClass}">${iconMarkup}${this.escapeHtml(key)}</span>`);
          };

          if (badges && badges.length) {
            badges.forEach((label) => pushTag(label));
          }
          pushTag(resolvedAge);
          if (meta.indigoexclusive) pushTag(labelIndigoExclusive);
          if (meta.islargeprint) pushTag(labelLargePrint);
          if (meta.pagecount) pushTag(`${meta.pagecount} ${labelPages}`);
          pushTag(productLanguage);
          if (meta.isKoboAudiobook && meta.hasKoboIsAbriged && meta.koboIsAbriged === false) {
            pushTag(labelUnabridged, { icon: audioTagIcon, modifier: 'audio' });
          }
          if (meta.isKoboAudiobook && meta.koboDuration) {
            pushTag(meta.koboDuration, { icon: durationTagIcon, modifier: 'duration', iconModifier: 'clock' });
          }

          if (fragments.length === 0) {
            tagsContainer.innerHTML = '';
            tagsContainer.setAttribute('hidden', 'true');
          } else {
            tagsContainer.innerHTML = fragments.join('');
            tagsContainer.removeAttribute('hidden');
          }
        };

        // Initial render
        renderTags(getSelectedVariantId());

        // Remove existing listener if it exists to prevent duplicates
        if (this.variantTagsHandler) {
          document.removeEventListener('variant:change', this.variantTagsHandler);
          this.variantTagsHandler = null;
        }

        // Update on variant change
        this.variantTagsHandler = (event) => {
          const variant = event?.detail?.variant;
          renderTags(variant?.id || getSelectedVariantId());
        };
        document.addEventListener('variant:change', this.variantTagsHandler);
      } catch (e) {
        // fail silently
      }
    }

    async fetchOverviewSection(variantId) {
      try {
        // Cancel any pending fetch request
        this.abortController?.abort();
        this.abortController = new AbortController();

        // Track the latest variant ID to ensure we only process the most recent request
        this.pendingVariantId = variantId;

        const productInfo = document.querySelector('product-info[id^="MainProduct-"]');
        if (!productInfo) {
          console.debug('[ProductOverview] Cannot fetch overview section: product-info element not found');
          return null;
        }

        const productUrl = productInfo.dataset?.url || window.location.pathname;
        const params = new URLSearchParams();
        if (variantId) params.set('variant', variantId);
        const pageUrl = `${productUrl}?${params.toString()}`;

        const response = await fetch(pageUrl, { signal: this.abortController.signal });

        // Check if this request is still the latest one
        if (this.pendingVariantId !== variantId) {
          console.debug('[ProductOverview] Fetch cancelled: newer variant request in progress', { cancelledVariantId: variantId, currentVariantId: this.pendingVariantId });
          return null;
        }

        if (!response.ok) {
          console.warn('[ProductOverview] Failed to fetch overview section: HTTP error', {
            status: response.status,
            statusText: response.statusText,
            url: pageUrl,
            variantId,
          });
          return null;
        }

        const htmlText = await response.text();

        // Check again after async operation to ensure this is still the latest request
        if (this.pendingVariantId !== variantId) {
          console.debug('[ProductOverview] Fetch cancelled after response: newer variant request in progress', { cancelledVariantId: variantId, currentVariantId: this.pendingVariantId });
          return null;
        }

        const parser = new DOMParser();
        const doc = parser.parseFromString(htmlText, 'text/html');

        // Try multiple selectors to find the overview section
        const overviewSection = doc.querySelector('[data-overview]')
          || doc.querySelector('.product__overview')
          || doc.querySelector('section [data-overview]');

        if (!overviewSection) {
          console.warn('[ProductOverview] Overview section not found in fetched HTML', {
            url: pageUrl,
            variantId,
            selectorsTried: ['[data-overview]', '.product__overview', 'section [data-overview]'],
          });
          return null;
        }

        // Final check before returning to ensure this is still the latest request
        if (this.pendingVariantId !== variantId) {
          console.debug('[ProductOverview] Fetch cancelled before returning: newer variant request in progress', { cancelledVariantId: variantId, currentVariantId: this.pendingVariantId });
          return null;
        }

        // Clone the element with all children
        // Return both the section and the variantId so caller can verify it's still current
        return {
          section: overviewSection.cloneNode(true),
          variantId,
        };
      } catch (e) {
        // Ignore AbortError as it's expected when cancelling requests
        if (e.name === 'AbortError') {
          console.debug('[ProductOverview] Fetch aborted (expected when cancelling requests)', { variantId });
          return null;
        }
        console.error('[ProductOverview] Unexpected error fetching overview section:', {
          error: e,
          message: e.message,
          stack: e.stack,
          variantId,
        });
        return null;
      }
    }

    insertOverviewSection(overviewSection) {
      if (!overviewSection) return false;

      // Check if overview already exists
      const existingOverview = document.querySelector('[data-overview]');
      if (existingOverview) {
        existingOverview.replaceWith(overviewSection);
      } else {
        // Find product-info element to insert after it
        const productInfo = document.querySelector('product-info[id^="MainProduct-"]')
          || document.querySelector('.main-product-section');
        if (!productInfo || !productInfo.parentNode) {
          return false;
        }
        productInfo.parentNode.insertBefore(
          overviewSection,
          productInfo.nextSibling,
        );
      }

      // Normalize before caching so resize/collapse retains semantic publisher paragraphs.
      normalizePublisherContent(overviewSection);
      // Update overview-specific DOM caches and behaviors without full re-initialization
      this.cacheTwoColumnContainers();
      this.refreshExpandableText();

      return true;
    }

    /**
     * Shared method to update the overview description section.
     * Handles fetching/inserting the overview section if needed, updating content,
     * managing visibility, and applying layout.
     * @param {string|null} description - The variant description HTML string
     * @param {number|null} variantId - The variant ID for fetching overview section
     * @returns {Promise<boolean>} - Returns true if update was successful, false otherwise
     */
    async updateOverviewDescription(description, variantId = null) {
      try {
        // Re-query elements in case DOM changed
        let overviewBodyEl = document.querySelector('.js-overview-body');

        // If overview body doesn't exist and we have a description, fetch it
        if (!overviewBodyEl && description && typeof description === 'string' && description.trim() !== '') {
          // If variantId not provided, try to get it from DOM
          if (!variantId) {
            try {
              const variantEl = document.querySelector('variant-selects [data-selected-variant]');
              if (variantEl) {
                variantId = JSON.parse(variantEl.textContent)?.id;
              }
            } catch (e) {
              // Log at debug level to aid troubleshooting while preserving fallback behavior
              console.debug('product-overview: Failed to parse selected variant JSON', e);
            }
          }

          const fetchResult = await this.fetchOverviewSection(variantId);
          if (!fetchResult || !fetchResult.section) {
            console.warn('[ProductOverview] Failed to fetch overview section for variant', { variantId });
            return false;
          }

          // Verify the returned section is still for the current pending variant
          // This prevents using stale data if a newer request started after fetch completed
          if (this.pendingVariantId !== fetchResult.variantId) {
            console.debug('[ProductOverview] Discarding stale overview section', {
              fetchedVariantId: fetchResult.variantId,
              currentVariantId: this.pendingVariantId,
            });
            return false;
          }

          if (!this.insertOverviewSection(fetchResult.section)) {
            console.warn('[ProductOverview] Failed to insert overview section into DOM', { variantId });
            return false;
          }

          // Wait for DOM to update, then re-query
          await new Promise((resolve) => {
            requestAnimationFrame(() => resolve());
          });
          overviewBodyEl = document.querySelector('[data-overview] .js-overview-body')
            || document.querySelector('.js-overview-body');
        }

        if (!overviewBodyEl) return false;

        const overviewContainer = overviewBodyEl.closest('[data-overview]') || overviewBodyEl.closest('.product__overview');
        const hasDescription = description && typeof description === 'string' && description.trim() !== '';

        if (hasDescription) {
          // Show the overview container
          if (overviewContainer) {
            overviewContainer.style.display = 'block';
            overviewContainer.removeAttribute('hidden');
            overviewContainer.removeAttribute('aria-hidden');
            overviewContainer.classList.remove('hide', 'hidden');
          }

          // Use innerHTML since description contains HTML (from variant_description_html)
          overviewBodyEl.innerHTML = description;
          normalizePublisherContent(overviewBodyEl);
          overviewBodyEl.dataset.originalMarkup = overviewBodyEl.innerHTML;
          overviewBodyEl.removeAttribute('data-columns-ready');
          this.cacheTwoColumnContainers();
          this.applyTwoColumnLayout(this.desktopMediaQuery?.matches ?? false);

          // Wait for layout to be computed before reading computed styles in checkTruncationNeeded
          await new Promise((resolve) => {
            requestAnimationFrame(() => resolve());
          });

          const readMoreBtn = document.querySelector('.js-read-more-btn');
          if (readMoreBtn) {
            this.checkTruncationNeeded(readMoreBtn);
          }
        } else {
          // Clear and hide when no description
          overviewBodyEl.textContent = '';
          overviewBodyEl.dataset.originalMarkup = '';
          overviewBodyEl.removeAttribute('data-columns-ready');
          if (overviewContainer) {
            overviewContainer.style.display = 'none';
            overviewContainer.setAttribute('hidden', 'true');
            overviewContainer.setAttribute('aria-hidden', 'true');
          }
        }

        return true;
      } catch (e) {
        console.error('[ProductOverview] Error updating overview description:', e);
        return false;
      }
    }

    setupVariantDescription() {
      try {
        // Remove existing listener if it exists to prevent duplicates
        if (this.variantDescriptionHandler) {
          document.removeEventListener('variant:change', this.variantDescriptionHandler);
          this.variantDescriptionHandler = null;
        }

        // Use shared utility function for consistency with product-info.js
        const utilityFn = window.ProductVariantUtils?.getSelectedVariantDescription;
        const getSelectedVariantDescription = utilityFn || function () {
          // Fallback implementation if utility not loaded - with error handling
          const selector = 'variant-selects [data-selected-variant-description]';
          const selectedVariantDescription = document.querySelector(selector)?.innerHTML;
          if (!selectedVariantDescription) {
            return null;
          }
          try {
            return JSON.parse(selectedVariantDescription);
          } catch (e) {
            console.warn('[ProductOverview] Failed to parse variant description JSON (fallback):', e);
            return null;
          }
        };

        // Create and store the handler
        this.variantDescriptionHandler = async (event) => {
          try {
            // Use microtask to ensure all synchronous DOM updates from event handlers complete
            await Promise.resolve();

            // Use variant from event detail if available to avoid DOM queries
            const variantId = event?.detail?.variant?.id || null;
            // Pass null to use document as root (global query)
            const description = getSelectedVariantDescription(null);
            await this.updateOverviewDescription(description, variantId);
          } catch (e) {
            console.error('[ProductOverview] Error updating variant description:', e);
          }
        };

        // Update on variant change
        document.addEventListener('variant:change', this.variantDescriptionHandler);
      } catch (e) {
        console.error('[ProductOverview] Error setting up variant description:', e);
      }
    }

    checkTruncationNeeded(button) {
      const textContainer = button.previousElementSibling;
      const textContent = textContainer.querySelector('.js-overview-body');
      const isLoveforReading = textContent.classList.contains('love_of_reading');
      const isDesktop = window.matchMedia(DESKTOP_MEDIA_QUERY).matches;

      if (isLoveforReading) {
        if (isDesktop) {
          // Disable read more for two-column layouts on desktop
          textContent.parentNode.classList.remove('truncated-text');
          button.style.display = 'none';
        } else {
          textContent.parentNode.classList.add('truncated-text');
          button.style.display = 'block';
        }
        return;
      }
      if (!textContent) return;

      // Get the desired number of lines from CSS custom property or default to 10
      const desiredLines = parseInt(getComputedStyle(textContainer).getPropertyValue('--truncate-lines') || '10', 10);

      // Get paragraph styling to account for margins in height calculation
      const firstParagraph = textContent.querySelector('p');
      let lineHeight;
      let marginBottom = 0;

      if (firstParagraph) {
        const pStyle = window.getComputedStyle(firstParagraph);
        const pLineHeight = parseFloat(pStyle.lineHeight);
        const pFontSize = parseFloat(pStyle.fontSize);
        lineHeight = Number.isNaN(pLineHeight) ? pFontSize * 1.6 : pLineHeight;
        marginBottom = parseFloat(pStyle.marginBottom) || 0;
      } else {
        const computedStyle = window.getComputedStyle(textContent);
        const cLineHeight = parseFloat(computedStyle.lineHeight);
        const fontSize = parseFloat(computedStyle.fontSize);
        lineHeight = Number.isNaN(cLineHeight) ? fontSize * 1.6 : cLineHeight;
      }

      // Calculate max-height that accounts for paragraph margins
      // Estimate: each paragraph averages 2-3 lines, so for 10 lines we might have 3-5 paragraphs
      // Each paragraph except the last has margin-bottom
      const estimatedParagraphs = Math.ceil(desiredLines / 2.5); // Rough estimate
      const totalMarginHeight = Math.max(0, estimatedParagraphs - 1) * marginBottom;
      const calculatedMaxHeight = (desiredLines * lineHeight) + totalMarginHeight;

      // Measure the constrained text wrapper height (collapsed state),
      // so we can still show the button when content is only slightly taller.
      const textContainerHeight = textContainer.getBoundingClientRect().height;

      // Measure full content height without constraints
      const originalWebkitLineClamp = textContent.style.webkitLineClamp;
      const originalLineClamp = textContent.style.lineClamp;
      const originalDisplay = textContent.style.display;

      textContent.style.webkitLineClamp = 'unset';
      textContent.style.lineClamp = 'unset';
      textContent.style.display = 'block';

      const fullHeight = textContent.getBoundingClientRect().height;

      // Restore original styles
      textContent.style.webkitLineClamp = originalWebkitLineClamp;
      textContent.style.lineClamp = originalLineClamp;
      textContent.style.display = originalDisplay;

      // Show/hide read more button and set appropriate max-height
      if (fullHeight > calculatedMaxHeight + OVERFLOW_TOLERANCE_PX) {
        button.style.display = 'block';
        // Set container max-height to allow exactly the desired lines + margins
        textContainer.style.setProperty('--calculated-max-height', `${calculatedMaxHeight}px`);
      } else if (fullHeight > textContainerHeight) {
        button.style.display = 'block';
      } else {
        button.style.display = 'none';
      }
    }

    toggleReadMore(button) {
      const textContainer = button.previousElementSibling;
      const textContent = textContainer.querySelector('.js-overview-body');
      const isExpanded = textContainer.classList.contains('expanded');
      const isDesktop = this.desktopMediaQuery?.matches ?? false;

      if (!textContent) return;

      // Prevent rapid clicks during animation
      if (button.disabled) return;

      const shouldAnimate = isDesktop && textContent.classList.contains('two-columns');
      if (shouldAnimate) {
        button.disabled = true;

        // Fade out
        textContent.style.transition = `opacity ${FADE_DURATION}ms ease-out`;
        textContent.style.opacity = '0';

        setTimeout(() => {
          textContainer.classList.toggle('expanded');
          textContainer.setAttribute('aria-expanded', String(!isExpanded));
          button.classList.toggle('expanded');
          button.setAttribute('aria-expanded', String(!isExpanded));
          this.applyTwoColumnLayout(true, { force: true });

          // Fade in
          requestAnimationFrame(() => {
            textContent.style.transition = `opacity ${FADE_DURATION}ms ease-in`;
            textContent.style.opacity = '1';

            // Clean up transition after animation completes
            setTimeout(() => {
              textContent.style.transition = '';
              textContent.style.opacity = '';
              button.disabled = false;
            }, FADE_DURATION);
          });
        }, FADE_DURATION);
      } else {
        // No animation on mobile or when not two-column layout
        textContainer.classList.toggle('expanded');
        textContainer.setAttribute('aria-expanded', String(!isExpanded));
        button.classList.toggle('expanded');
        button.setAttribute('aria-expanded', String(!isExpanded));
      }

      // Smooth scroll if collapsing and text is out of view
      if (isExpanded) {
        setTimeout(() => {
          const containerTop = textContainer.getBoundingClientRect().top;
          if (containerTop < 0) {
            textContainer.scrollIntoView({ behavior: 'smooth' });
          }
        }, shouldAnimate ? TOTAL_ANIMATION_DURATION : 0);
      }
    }

    handleResize() {
      // Re-evaluate truncation for all read more buttons on resize
      this.applyTwoColumnLayout(this.desktopMediaQuery?.matches ?? false, { force: true });
      this.refreshExpandableText();
    }
  }

  function initProductOverview() {
    if (productOverviewInstance) {
      productOverviewInstance.init();
      return;
    }
    productOverviewInstance = new ProductOverview();
  }

  window.initProductOverview = initProductOverview;

  // Expose methods for external use
  window.getProductOverviewInstance = () => productOverviewInstance;

  // Initialize on document ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initProductOverview();
    });
  } else {
    initProductOverview();
  }
}());
